import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Avatar,
  Divider,
} from "@heroui/react";
import { Plus, Trash, Pencil, UserCircle, Shield } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useUsers } from 'shared/contexts/users.context';
import { usePermissions } from 'shared/contexts/permissions.context';
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";

interface ClerkUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email_addresses?: Array<{ email_address: string; id: string; verification?: any }>;
  image_url?: string;
  profile_image_url?: string;
  public_metadata?: {
    admin?: boolean;
    internal?: boolean;
    [key: string]: any;
  };
  object?: string;
  created_at?: number;
  updated_at?: number;
  last_sign_in_at?: number;
  username?: string | null;
}

interface UserWithRole extends ClerkUser {
  role?: string;
}

interface Role {
  id: number;
  name: string;
  description?: string;
  permissions: string[];
  userCount: number;
  createdAt?: string;
  updatedAt?: string;
}

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

export const RollenbeheerTab: React.FC = () => {
  const { users: contextUsers } = useUsers();
  const { session } = useSession();
  const { refreshPermissions } = usePermissions();
  const [usersWithRoles, setUsersWithRoles] = useState<UserWithRole[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });

  // Helper function to convert boolean permissions to array
  const getPermissionsFromRole = (role: any): string[] => {
    const permissionKeys = [
      'canAccessDashboard',        // Dashboard
      'canAccessContacts',         // Contacten
      'canAccessAcquisitions',     // Acquisities
      'canAccessDueDiligence',     // Due diligence
      'canAccessPictures',         // Foto's
      'canAccessDocuments',        // Documenten
      'canAccessClusters',         // Clusters
      'canAccessBuildings',        // Gebouwen
      'canAccessUnits',            // Units
      'canAccessBuildingAttributes', // Gebouw attributen
      'canAccessRental',           // Verhuur
      'canAccessHelpdesk',         // Helpdesk
      'canAccessConversations',    // Conversaties
      'canAccessMaintenance',      // Onderhoud
      'canAccessInsurance',        // Verzekeringen
      'canAccessRenovation',       // Renovatie
      'canAccessTasks',            // Taken
      'canAccessNotes',            // Notities
      'canAccessBugReports',       // Bug Reports (Algemeen)
      'canAccessGeneral',          // General module (Bugs & Roadmap)
      'canAccessCorporate',        // Corporate module
      'canEditCorporate',          // Corporate edit (future)
      'canAccessSettings',         // Instellingen
      'canEditUnitIds'             // Unit ID editing (admin-only)
    ];

    return permissionKeys.filter(key => role[key] === true);
  };

  // Helper function to convert permissions array back to boolean flags
  const convertPermissionsToFlags = (permissions: string[]): any => {
    const flags: any = {};
    const allPermissionKeys = [
      'canAccessDashboard',        // Dashboard
      'canAccessContacts',         // Contacten
      'canAccessAcquisitions',     // Acquisities
      'canAccessDueDiligence',     // Due diligence
      'canAccessPictures',         // Foto's
      'canAccessDocuments',        // Documenten
      'canAccessClusters',         // Clusters
      'canAccessBuildings',        // Gebouwen
      'canAccessUnits',            // Units
      'canAccessBuildingAttributes', // Gebouw attributen
      'canAccessRental',           // Verhuur
      'canAccessHelpdesk',         // Helpdesk
      'canAccessConversations',    // Conversaties
      'canAccessMaintenance',      // Onderhoud
      'canAccessInsurance',        // Verzekeringen
      'canAccessRenovation',       // Renovatie
      'canAccessTasks',            // Taken
      'canAccessNotes',            // Notities
      'canAccessBugReports',       // Bug Reports (Algemeen)
      'canAccessGeneral',          // General module (Bugs & Roadmap)
      'canAccessCorporate',        // Corporate module
      'canEditCorporate',          // Corporate edit (future)
      'canAccessSettings',         // Instellingen
      'canEditUnitIds'             // Unit ID editing (admin-only)
    ];

    allPermissionKeys.forEach(key => {
      flags[key] = permissions.includes(key);
    });

    return flags;
  };

  // Map context users to users with roles when context users change
  useEffect(() => {
    if (contextUsers && contextUsers.length > 0) {
      console.log('Rollenbeheer: Mapping context users:', contextUsers.length);
      // Initial mapping - will be updated when we fetch actual user permissions
      const mappedUsers: UserWithRole[] = contextUsers.map((user: ClerkUser) => ({
        ...user,
        role: undefined // Will be populated when fetching user permissions
      }));
      setUsersWithRoles(mappedUsers);
      console.log('Rollenbeheer: Mapped users with roles:', mappedUsers.length);
      
      // Fetch actual user permissions from backend
      fetchUserPermissions();
    }
  }, [contextUsers]);

  useEffect(() => {
    Promise.all([
      fetchRoles(),
      fetchPermissions(),
    ]).finally(() => setIsLoading(false));
  }, []);



  const fetchRoles = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/roles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }
      
      const apiRoles = await response.json();
      
      // Transform API response to match frontend interface
      const transformedRoles: Role[] = apiRoles.map((role: any) => ({
        id: role.id,
        name: role.name,
        description: role.description || '',
        permissions: getPermissionsFromRole(role), // Convert boolean permissions to array
        userCount: role.userCount || 0,
        createdAt: role.createdAt,
        updatedAt: role.updatedAt,
      }));
      
      setRoles(transformedRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Fout bij ophalen van rollen');
      setRoles([]);
    }
  };

  const fetchPermissions = async () => {
    try {
      // TODO: Replace with actual API endpoint
      // const response = await fetchWithAuth(`${API_URL}/permissions`, {
      //   method: 'GET',
      //   headers: {
      //     'Content-Type': 'application/json'
      //   }
      // }, session);
      
      // Mock data for the main navigation tabs
      const mockPermissions: Permission[] = [
        { id: 'canAccessDashboard', name: 'Dashboard', description: 'Toegang tot dashboard', category: 'navigatie' },
        { id: 'canAccessContacts', name: 'Contacten', description: 'Toegang tot contacten', category: 'navigatie' },
        { id: 'canAccessAcquisitions', name: 'Acquisities', description: 'Toegang tot acquisities', category: 'navigatie' },
        { id: 'canAccessDueDiligence', name: 'Due Diligence', description: 'Toegang tot due diligence', category: 'navigatie' },
        { id: 'canAccessPictures', name: 'Foto\'s', description: 'Toegang tot foto\'s', category: 'navigatie' },
        { id: 'canAccessDocuments', name: 'Documenten', description: 'Toegang tot documenten', category: 'navigatie' },
        { id: 'canAccessClusters', name: 'Clusters', description: 'Toegang tot clusters', category: 'navigatie' },
        { id: 'canAccessBuildings', name: 'Gebouwen', description: 'Toegang tot gebouwen', category: 'navigatie' },
        { id: 'canAccessUnits', name: 'Units', description: 'Toegang tot units', category: 'navigatie' },
        { id: 'canAccessBuildingAttributes', name: 'Gebouw Attributen', description: 'Toegang tot gebouw attributen', category: 'navigatie' },
        { id: 'canAccessRental', name: 'Verhuur', description: 'Toegang tot verhuur', category: 'navigatie' },
        { id: 'canAccessHelpdesk', name: 'Helpdesk', description: 'Toegang tot helpdesk', category: 'navigatie' },
        { id: 'canAccessConversations', name: 'Conversaties', description: 'Toegang tot conversaties', category: 'navigatie' },
        { id: 'canAccessMaintenance', name: 'Onderhoud', description: 'Toegang tot onderhoud', category: 'navigatie' },
        { id: 'canAccessInsurance', name: 'Verzekeringen', description: 'Toegang tot verzekeringen', category: 'navigatie' },
        { id: 'canAccessRenovation', name: 'Renovatie', description: 'Toegang tot renovatie', category: 'navigatie' },
        { id: 'canAccessTasks', name: 'Taken', description: 'Toegang tot taken', category: 'navigatie' },
        { id: 'canAccessNotes', name: 'Notities', description: 'Toegang tot notities', category: 'navigatie' },
        { id: 'canAccessBugReports', name: 'Bug Reports', description: 'Toegang tot bug reports en feature requests', category: 'algemeen' },
        { id: 'canAccessGeneral', name: 'Algemene Module', description: 'Toegang tot bugs & roadmap', category: 'algemeen' },
        { id: 'canAccessCorporate', name: 'Corporate Module', description: 'Toegang tot corporate functionaliteiten', category: 'corporate' },
        { id: 'canEditCorporate', name: 'Corporate Bewerken', description: 'Bewerken van corporate gegevens (toekomstig)', category: 'corporate' },
        { id: 'canAccessSettings', name: 'Instellingen', description: 'Toegang tot instellingen', category: 'navigatie' },
        { id: 'canEditUnitIds', name: 'Unit ID Bewerken', description: 'Wijzigen van unit IDs (admin-only operatie)', category: 'admin' },
      ];
      
      setPermissions(mockPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Fout bij ophalen van permissies');
      setPermissions([]);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      console.log(`[Frontend] Fetching user permissions...`);
      const response = await fetchWithAuth(`${API_URL}/roles/users/permissions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user permissions');
      }
      
      const userPermissions = await response.json();
      console.log(`[Frontend] Received user permissions:`, userPermissions);
      
      // Update users with their actual assigned roles
      setUsersWithRoles(prev => {
        const updated = prev.map(user => {
          const userPermission = userPermissions.find((up: any) => up.userId === user.id);
          const newRole = userPermission ? userPermission.roleId.toString() : undefined;
          console.log(`[Frontend] User ${user.id} role: ${user.role} -> ${newRole}`);
          return {
            ...user,
            role: newRole
          };
        });
        console.log(`[Frontend] Updated users with roles:`, updated);
        return updated;
      });
      
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      toast.error('Fout bij ophalen van gebruikerspermissies');
    }
  };

  const handleUserRoleChange = async (userId: string, newRoleId: string) => {
    try {
      console.log(`[Frontend] Assigning role ${newRoleId} to user ${userId}`);
      
      const requestBody = { 
        userId: userId,
        roleId: parseInt(newRoleId, 10) // Convert string to number for backend
      };
      console.log(`[Frontend] Request body:`, requestBody);
      
      const response = await fetchWithAuth(`${API_URL}/roles/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }, session);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Frontend] Failed to assign role. Status: ${response.status}, Error: ${errorText}`);
        throw new Error('Failed to assign role');
      }
      
      const result = await response.json();
      console.log(`[Frontend] Role assignment response:`, result);
      
      // Refresh user permissions from backend to ensure consistency
      console.log(`[Frontend] Refreshing user permissions...`);
      await fetchUserPermissions();
      
      // Also refresh roles to update user counts
      console.log(`[Frontend] Refreshing roles...`);
      await fetchRoles();
      
      // Refresh global permissions context for the current user if their role was changed
      await refreshPermissions();
      
      toast.success('Rol succesvol bijgewerkt');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Fout bij bijwerken van rol');
    }
  };

  const getUserEmail = (user: ClerkUser): string => {
    if (user.email_addresses && user.email_addresses.length > 0) {
      return user.email_addresses[0].email_address;
    }
    return 'Geen email';
  };

  const getUserDisplayName = (user: ClerkUser): string => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) {
      return user.first_name;
    }
    return getUserEmail(user);
  };

  const handleCreateRole = async () => {
    try {
      // Convert permissions array back to boolean fields for API
      const roleData = {
        name: newRole.name,
        description: newRole.description,
        ...convertPermissionsToFlags(newRole.permissions)
      };

      const response = await fetchWithAuth(`${API_URL}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      }, session);
      
      if (!response.ok) {
        throw new Error('Failed to create role');
      }
      
      // Refresh roles list and user permissions
      await Promise.all([
        fetchRoles(),
        fetchUserPermissions()
      ]);
      
      setNewRole({ name: '', description: '', permissions: [] });
      setIsRoleModalOpen(false);
      toast.success('Rol succesvol aangemaakt');
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error('Fout bij aanmaken van rol');
    }
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions,
    });
    setIsRoleModalOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      // Convert permissions array back to boolean fields for API
      const roleData = {
        name: newRole.name,
        description: newRole.description,
        ...convertPermissionsToFlags(newRole.permissions)
      };

      const response = await fetchWithAuth(`${API_URL}/roles/${editingRole.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      }, session);
      
      if (!response.ok) {
        throw new Error('Failed to update role');
      }
      
      // Refresh roles list and user permissions
      await Promise.all([
        fetchRoles(),
        fetchUserPermissions()
      ]);
      
      setEditingRole(null);
      setNewRole({ name: '', description: '', permissions: [] });
      setIsRoleModalOpen(false);
      toast.success('Rol succesvol bijgewerkt');
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Fout bij bijwerken van rol');
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Weet je zeker dat je deze rol wilt verwijderen?')) return;

    try {
      const response = await fetchWithAuth(`${API_URL}/roles/${roleId}`, {
        method: 'DELETE'
      }, session);
      
      if (!response.ok) {
        throw new Error('Failed to delete role');
      }
      
      // Refresh roles list and user permissions
      await Promise.all([
        fetchRoles(),
        fetchUserPermissions()
      ]);
      toast.success('Rol succesvol verwijderd');
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Fout bij verwijderen van rol');
    }
  };

  const closeRoleModal = () => {
    setIsRoleModalOpen(false);
    setEditingRole(null);
    setNewRole({ name: '', description: '', permissions: [] });
  };

  const getPermissionName = (permissionId: string) => {
    const permission = permissions.find(p => p.id === permissionId);
    return permission ? permission.name : permissionId;
  };

  const getRoleColor = (roleId?: string) => {
    if (!roleId) return 'default';
    
    // Find the role to determine color based on role name or other properties
    const role = roles.find(r => r.id.toString() === roleId);
    if (!role) return 'default';
    
    // Color logic based on role name or can be customized
    const roleName = role.name.toLowerCase();
    if (roleName.includes('admin')) return 'danger';
    if (roleName.includes('intern') || roleName.includes('manager')) return 'primary';
    if (roleName.includes('extern') || roleName.includes('gast') || roleName.includes('guest')) return 'secondary';
    return 'default';
  };

  if (isLoading || (contextUsers.length === 0 && usersWithRoles.length === 0)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-secondary-mid">Gebruikers en rollen laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Users Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <div className="flex flex-row items-center gap-1.5">
              <UserCircle className="h-[18px] w-[18px]" />
              <h2 className="text-lg font-medium text-neutral-primary">Gebruikersbeheer</h2>
            </div>
            <p className="text-sm text-neutral-secondary-mid">Beheer gebruikers en wijs rollen toe</p>
          </div>
        </div>

        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <div className="text-blue-600 mt-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-1">Gebruikersrollen</h3>
              <p className="text-sm text-blue-700">
                Wijs rollen toe aan gebruikers. Admin heeft volledige toegang, Interne medewerkers hebben uitgebreide toegang, en Externe gebruikers hebben beperkte toegang.
              </p>
            </div>
          </div>
        </div>

        <Table
          classNames={{
            wrapper: "shadow-none rounded-none p-0",
            thead: "h-fit border-b-2 border-neutral-primary/40",
            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
            tbody: "border-b divide-y divide-neutral-primary/15",
            tr: "divide-x divide-neutral-primary/15",
            td: "py-2.5",
          }}
        >
          <TableHeader>
            <TableColumn>Gebruiker</TableColumn>
            <TableColumn>Email</TableColumn>
            <TableColumn>Huidige Rol</TableColumn>
            <TableColumn>Rol Toewijzen</TableColumn>
          </TableHeader>
          <TableBody>
            {usersWithRoles.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar
                      size="sm"
                      src={user.image_url || user.profile_image_url || `https://api.clerk.com/v1/users/${user.id}/image`}
                      name={getUserDisplayName(user)}
                      className="shrink-0"
                    />
                    <div>
                      <div className="font-medium text-neutral-primary">
                        {getUserDisplayName(user)}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-neutral-secondary-mid">
                    {getUserEmail(user)}
                  </div>
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={getRoleColor(user.role) as any}
                  >
                    {user.role ? roles.find(r => r.id.toString() === user.role)?.name || user.role : 'Geen rol'}
                  </Chip>
                </TableCell>
                <TableCell>
                  <Select
                    size="sm"
                    placeholder="Selecteer rol"
                    selectedKeys={user.role ? new Set([user.role.toString()]) : new Set()}
                    onSelectionChange={(keys) => {
                      const selectedRole = Array.from(keys)[0] as string;
                      if (selectedRole && selectedRole !== user.role) {
                        console.log(`[Frontend] User selection changed from ${user.role} to ${selectedRole}`);
                        handleUserRoleChange(user.id, selectedRole);
                      }
                    }}
                    className="w-32"
                  >
                    {roles.map((role) => (
                      <SelectItem key={role.id.toString()}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Divider />

      {/* Roles Management Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <div className="flex flex-row items-center gap-1.5">
              <Shield className="h-[18px] w-[18px]" />
              <h2 className="text-lg font-medium text-neutral-primary">Rollenbeheer</h2>
            </div>
            <p className="text-sm text-neutral-secondary-mid">Beheer rollen en hun permissies</p>
          </div>
          <Button
            color="primary"
            startContent={<Plus />}
            onPress={() => setIsRoleModalOpen(true)}
            className="bg-brick-primary text-brick-secondary-fine"
          >
            Rol toevoegen
          </Button>
        </div>

        <Table
          classNames={{
            wrapper: "shadow-none rounded-none p-0",
            thead: "h-fit border-b-2 border-neutral-primary/40",
            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
            tbody: "border-b divide-y divide-neutral-primary/15",
            tr: "divide-x divide-neutral-primary/15",
            td: "py-2.5",
          }}
        >
          <TableHeader>
            <TableColumn>Rolnaam</TableColumn>
            <TableColumn>Beschrijving</TableColumn>
            <TableColumn>Permissies</TableColumn>
            <TableColumn>Gebruikers</TableColumn>
            <TableColumn>Acties</TableColumn>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>
                  <div className="font-medium">{role.name}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-neutral-secondary-mid">{role.description}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.map((permission) => (
                      <Chip
                        key={permission}
                        size="sm"
                        variant="flat"
                        color="primary"
                        className="bg-brick-primary/10 text-brick-primary"
                      >
                        {getPermissionName(permission)}
                      </Chip>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{usersWithRoles.filter(u => u.role === role.id.toString()).length} gebruikers</div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Bewerken">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleEditRole(role)}
                      >
                        <Pencil size={16} className="text-moss-primary" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Verwijderen">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => handleDeleteRole(role.id.toString())}
                      >
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Role Modal */}
      <Modal 
        isOpen={isRoleModalOpen} 
        onClose={closeRoleModal}
        size="2xl"
      >
        <ModalContent>
          <ModalHeader>
            {editingRole ? 'Rol bewerken' : 'Nieuwe rol aanmaken'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Rolnaam"
                placeholder="Voer rolnaam in"
                value={newRole.name}
                onValueChange={(value) => setNewRole({ ...newRole, name: value })}
                isRequired
              />
              
              <Input
                label="Beschrijving"
                placeholder="Voer beschrijving in"
                value={newRole.description}
                onValueChange={(value) => setNewRole({ ...newRole, description: value })}
              />
              
              <Select
                label="Permissies"
                placeholder="Selecteer permissies"
                selectionMode="multiple"
                selectedKeys={new Set(newRole.permissions)}
                onSelectionChange={(keys) => {
                  const selectedPermissions = Array.from(keys) as string[];
                  setNewRole({ ...newRole, permissions: selectedPermissions });
                }}
              >
                {permissions.map((permission) => (
                  <SelectItem key={permission.id}>
                    {permission.name} - {permission.description}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={closeRoleModal}>
              Annuleren
            </Button>
            <Button 
              color="primary" 
              onPress={editingRole ? handleUpdateRole : handleCreateRole}
              isDisabled={!newRole.name}
              className="bg-brick-primary text-brick-secondary-fine"
            >
              {editingRole ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};